/**
 * The template browser (P7, restyled and embedded in R2): a first run that
 * starts from something real.
 *
 * The templates down the left — category chips, a search box, one card per
 * template — and a preview on the right that draws the template's FIRST PAGE
 * through the real FormRenderer, read-only, before anything is created. The
 * card says what the template needs beyond the free plan (a payment field
 * needs Stripe Connect; a signature is free) so nobody builds a form around
 * a field that cannot take an answer yet.
 *
 * Since R2 it is one MODE of the New form modal (`NewFormModal.tsx`) rather
 * than a modal of its own: the modal owns the name, the folder, the create
 * request and the plan-limit modal a refusal opens; the browser owns the
 * choosing and reports the chosen template up through `onSelected`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FormTemplateDetail, FormTemplateSummary, TemplateCategory, getFormTemplateApi, listFormTemplates,
} from '../../api/forms';
import { ConnectStatus, getPaymentsStatus } from '../../api/admin';
import { PHONE, useMediaQuery } from '../../shared/media';
import FormRenderer from '../../form-engine/FormRenderer';
import { createInitialState, type FormState, type PublishedDef } from '../../form-engine/model';
import { derivePages } from '../../form-engine/pages';
import '../../form.css';

const ALL = 'all';

/** Does the query hit the name, the description, a tag or the category? */
function matches(t: FormTemplateSummary, categoryLabel: string, q: string): boolean {
  if (!q) return true;
  const hay = [t.name, t.description, categoryLabel, ...t.tags].join(' ').toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every((word) => hay.includes(word));
}

/** The footer's one-line note: one draft, or for a pair the two linked ones. */
export function templateNote(t: FormTemplateSummary | null): string {
  if (t?.followUp) {
    return `Creates two linked drafts — “${t.name}” for you and “${t.followUp.name}” for the customer. Publish both.`;
  }
  return 'Opens as a draft. Nothing goes live until you publish.';
}

export default function TemplateBrowser({ onSelected, onLoaded }: {
  /** the template being previewed, or null while nothing is (nothing loaded, nothing matches) */
  onSelected: (t: FormTemplateSummary | null) => void;
  /** how many templates there are, for the mode card's "{n} ready-made forms" */
  onLoaded?: (count: number) => void;
}) {
  const [templates, setTemplates] = useState<FormTemplateSummary[] | null>(null);
  const [categories, setCategories] = useState<TemplateCategory[]>([]);
  const [error, setError] = useState('');
  const [category, setCategory] = useState(ALL);
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [details, setDetails] = useState<Record<string, FormTemplateDetail>>({});
  const [payments, setPayments] = useState<ConnectStatus | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  /*
    D3 — the phone's two steps. A list beside a preview does not fit a 375px
    screen, so the two panes become two screens: pick, then look. The desktop
    still renders both at once; `phone` is false there and `step` is never read.
  */
  const phone = useMediaQuery(PHONE);
  const [step, setStep] = useState<1 | 2>(1);

  useEffect(() => {
    listFormTemplates()
      .then((r) => {
        setTemplates(r.templates);
        setCategories(r.categories || []);
        if (r.templates[0]) setSelectedId(r.templates[0].id);
        onLoaded?.(r.templates.length);
      })
      .catch((e) => setError(e.message || 'Could not load the templates'));
    // Only to word the payment note honestly; the browser works without it.
    getPaymentsStatus().then((r) => setPayments(r.status)).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId || details[selectedId]) return;
    let stale = false;
    getFormTemplateApi(selectedId)
      .then((r) => { if (!stale) setDetails((d) => ({ ...d, [r.template.id]: r.template })); })
      .catch((e) => { if (!stale) setError(e.message || 'Could not load the preview'); });
    return () => { stale = true; };
  }, [selectedId, details]);

  const labelOf = (id: string) => categories.find((c) => c.id === id)?.label || id;

  const visible = useMemo(() => (templates || []).filter((t) =>
    (category === ALL || t.category === category) && matches(t, labelOf(t.category), q)), [templates, category, q, categories]);

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of templates || []) m.set(t.category, (m.get(t.category) || 0) + 1);
    return m;
  }, [templates]);

  // A card that the chip or the search has just hidden is not the one being
  // previewed: the selection follows the visible list.
  useEffect(() => {
    if (!templates) return;
    if (!visible.some((t) => t.id === selectedId)) setSelectedId(visible[0]?.id || '');
  }, [visible, selectedId, templates]);

  // A new template starts at the top of its page, not wherever the last one was scrolled to.
  useEffect(() => { previewRef.current?.scrollTo?.({ top: 0 }); }, [selectedId]);

  const selected = templates?.find((t) => t.id === selectedId) || null;
  useEffect(() => { onSelected(selected); }, [selected, onSelected]);

  const detail = selectedId ? details[selectedId] : undefined;
  const stripeReady = !!payments && payments.connected && payments.chargesEnabled;

  /** The card's one-line gate note. Signature is free and says so; payment names Stripe Connect. */
  const needsNote = (t: FormTemplateSummary) => {
    const notes: { text: string; tone: 'caution' | 'ok' | 'neutral' }[] = [];
    if (t.needs.includes('payments')) {
      notes.push(stripeReady
        ? { text: 'Takes payment · Stripe connected', tone: 'ok' }
        : { text: 'Takes payment · needs Stripe Connect', tone: 'caution' });
    }
    if (t.tags.includes('signature')) notes.push({ text: 'Signature · free on every plan', tone: 'neutral' });
    if (t.followUp) notes.push({ text: 'Two forms · you start, the customer finishes by private link', tone: 'neutral' });
    return notes;
  };

  /** "N questions · M pages", or for a pair the two forms' counts side by side. */
  const countLine = (t: FormTemplateSummary) => {
    if (t.followUp) return `${t.fieldCount} + ${t.followUp.fieldCount} questions · 2 forms`;
    return `${t.fieldCount} ${t.fieldCount === 1 ? 'question' : 'questions'}${t.pageCount > 1 ? ` · ${t.pageCount} pages` : ''}`;
  };

  const onPhoneStep2 = phone && step === 2;

  return (
    <div className="nf-browser">
      <div className="tg-list" hidden={onPhoneStep2}>
        {error && <div className="ad-err" style={{ margin: '12px 16px 0' }}>{error}</div>}
        <div className="tg-filters">
          <input
            className="fs-input"
            placeholder="Search templates…"
            aria-label="Search templates"
            value={q}
            // Typing searches everything: a chip left on from before would
            // quietly turn a search into "nothing matches".
            onChange={(e) => { setQ(e.target.value); setCategory(ALL); }}
          />
          <div className="tg-chips" role="group" aria-label="Category">
            <button type="button" className={`tg-chip${category === ALL ? ' on' : ''}`} aria-pressed={category === ALL} onClick={() => setCategory(ALL)}>
              All <span className="count">{templates?.length ?? ''}</span>
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`tg-chip${category === c.id ? ' on' : ''}`}
                aria-pressed={category === c.id}
                onClick={() => setCategory(c.id)}
              >
                {c.label} <span className="count">{counts.get(c.id) || 0}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="tg-cards">
          {!templates ? (
            <div className="fs-cell-sub" style={{ padding: 16 }}>Loading templates…</div>
          ) : !visible.length ? (
            <div className="fs-cell-sub" style={{ padding: 16 }}>
              No templates match {q ? <>“{q}”</> : 'this category'}.
            </div>
          ) : visible.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`tg-card${t.id === selectedId ? ' on' : ''}`}
              aria-pressed={t.id === selectedId}
              onClick={() => { setSelectedId(t.id); if (phone) setStep(2); }}
            >
              <div className="tg-card-head">
                <b>{t.name}</b>
                <span className="fs-cell-sub faint">{countLine(t)}</span>
              </div>
              <div className="fs-cell-sub">{t.description}</div>
              {!!needsNote(t).length && (
                <div className="tg-card-needs">
                  {needsNote(t).map((n) => <span key={n.text} className={`fs-pill ${n.tone}`}>{n.text}</span>)}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="tg-preview" aria-label="Template preview" ref={previewRef} hidden={phone && step === 1}>
        {onPhoneStep2 && (
          <div className="tg-step2-title">
            <button type="button" className="tg-back" aria-label="Back to the templates" onClick={() => setStep(1)}>‹</button>
            {selected?.name || 'Preview'}
          </div>
        )}
        {!selected ? null : !detail ? (
          <div className="fs-cell-sub" style={{ padding: 24 }}>Loading the preview…</div>
        ) : (
          <TemplatePreview key={detail.id} detail={detail} />
        )}
      </div>
    </div>
  );
}

/** The template's first page through the real renderer, every control disabled. */
function TemplatePreview({ detail }: { detail: FormTemplateDetail }) {
  const def = detail.definition as PublishedDef;
  const pages = useMemo(() => derivePages(def.layout.sections), [def]);
  const [state, setState] = useState<FormState>(() => createInitialState(def, {}));
  const first = pages[0];
  return (
    <>
      <div className="tg-preview-note fs-cell-sub faint">
        {pages.length > 1
          ? `Preview of page 1 of ${pages.length} — “${first.title || first.sections[0]?.title || 'Page 1'}”. Read-only.`
          : 'Preview — read-only.'}
      </div>
      <div className="of-container tg-paper">
        <h1>{detail.name}</h1>
        <p className="of-subtitle">{detail.description}</p>
        <FormRenderer
          def={def}
          state={state}
          onState={(update) => setState((s) => update(s))}
          cfg={{}}
          idPrefix={`tpl-${detail.id}`}
          readOnly
          sections={first?.sections}
          formName={detail.name}
          requiredStyle={detail.settings?.requiredStyle}
        />
      </div>
    </>
  );
}
